import { useMemo, useState } from 'react';
import { SearchParamsValue } from '@apps/stays/components/SearchParamsObserver/SearchParamsObserver';
import { formatDateComponentsToString } from '@shared/utils/dateHelpers';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useQueryParamHelper } from '@shared/core/queryString';

const IS_SKIP_IF_NO_SEARCH_PARAMS = 'skipIfNoSearchParams';

/**
 * Custom hook to handle URL parameters for hotel booking redirects
 * Manages search params state and generates redirect overrides for dates and occupancy
 */
export const useHotelBookingRedirectOverrides = () => {
  const [searchParams, setSearchParams] = useState<SearchParamsValue | null>(null);
  const queryParamHelper = useQueryParamHelper();

  const handleSearchParamsUpdate = useEventCallback((value: SearchParamsValue) => {
    setSearchParams(value);
  });

  const redirectOverrides = useMemo(() => {
    if (!searchParams) return undefined;
    return {
      dates:
        searchParams.startDate.valid && searchParams.endDate.valid
          ? {
              checkIn: formatDateComponentsToString(searchParams.startDate.value),
              checkOut: formatDateComponentsToString(searchParams.endDate.value),
            }
          : undefined,
      occupancies: searchParams.rooms.valid
        ? {
            occupancies: searchParams.rooms.value.map(room => ({
              adults: room.adults,
              childrenAges: room.childrenAges,
            })),
            unitCount: searchParams.rooms.value.length,
          }
        : undefined,
    };
  }, [searchParams]);

  return {
    searchParams,
    handleSearchParamsUpdate,
    redirectOverrides,
    // We should skip the API call when the search params are invalid and the skipIfNoSearchParams query param is set to true. This is needed because some redirects include search params while others don’t. The API should only be triggered once the search params state is properly set for those cases that include them.
    skipIfNotValidSearchParams:
      (!searchParams?.startDate.valid || !searchParams?.endDate.valid || !searchParams?.rooms.valid) && queryParamHelper.getValueString(IS_SKIP_IF_NO_SEARCH_PARAMS) === 'true',
  };
};
